// Nurly — collection / recherche : tiroir de filtres (mise à jour instantanée comme le site d'origine), curseur de prix, tri.
// Sans JS, le formulaire GET du tiroir et les liens fonctionnent normalement.

class NurlyPriceRange extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.max = Number(this.dataset.max) || 0;
    this.thumbs = { min: this.querySelector('[data-thumb="min"]'), max: this.querySelector('[data-thumb="max"]') };
    this.inputs = { min: this.querySelector('[data-input="min"]'), max: this.querySelector('[data-input="max"]') };
    this.range = this.querySelector('[data-range]');
    ['min', 'max'].forEach((k) => {
      const other = k === 'min' ? 'max' : 'min';
      this.thumbs[k]?.addEventListener('input', () => {
        let v = Number(this.thumbs[k].value);
        const o = Number(this.thumbs[other].value);
        if (k === 'min' && v > o) v = o;
        if (k === 'max' && v < o) v = o;
        this.thumbs[k].value = v;
        this.inputs[k].value = v;
        this.paint();
      });
      // Relâchement du curseur → on prévient le formulaire (une seule mise à jour)
      this.thumbs[k]?.addEventListener('change', () => this.inputs[k].dispatchEvent(new Event('change', { bubbles: true })));
      this.inputs[k]?.addEventListener('input', () => {
        const v = Math.min(Math.max(Number(this.inputs[k].value) || 0, 0), this.max);
        this.thumbs[k].value = v;
        this.paint();
      });
    });
    this.paint();
  }
  paint() {
    if (!this.range || !this.max) return;
    const a = Number(this.thumbs.min.value), b = Number(this.thumbs.max.value);
    this.range.style.left = (Math.min(a, b) / this.max) * 100 + '%';
    this.range.style.right = 100 - (Math.max(a, b) / this.max) * 100 + '%';
  }
}
if (!customElements.get('nurly-price-range')) customElements.define('nurly-price-range', NurlyPriceRange);

// Paramètres du formulaire sans les champs vides ni la fourchette de prix complète (URL propres)
function nurlyFormParams(form) {
  const params = new URLSearchParams();
  const price = form.querySelector('nurly-price-range');
  const max = price ? Number(price.dataset.max) : null;
  new FormData(form).forEach((value, key) => {
    const v = String(value).trim();
    if (v === '') return;
    if (price && /price\.gte$/.test(key) && Number(v) <= 0) return;
    if (price && /price\.lte$/.test(key) && Number(v) >= max) return;
    params.append(key, v);
  });
  return params;
}

class NurlyFacetsDrawer extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.form = this.querySelector('[data-nurly-facets-form]');
    this.querySelectorAll('[data-nurly-drawer-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
    if (this.form) {
      this.form.addEventListener('change', () => {
        clearTimeout(this._t);
        this._t = setTimeout(() => this.refresh(), 300);
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const url = this.url();
        window.location.assign(url);
      });
    }
    // Compatibilité avec les liens du site d'origine : /collections/x#drawer-filter ouvre les filtres
    if (window.location.hash === '#drawer-filter') requestAnimationFrame(() => this.open());
  }
  url() {
    const qs = nurlyFormParams(this.form).toString();
    return this.form.getAttribute('action') + (qs ? '?' + qs : '');
  }
  async refresh() {
    const url = this.url();
    const sep = url.includes('?') ? '&' : '?';
    const id = this.dataset.sectionId;
    if (!id) return;
    this._ctrl?.abort();
    this._ctrl = new AbortController();
    const results = document.querySelector(`#nurly-cgrid-${CSS.escape(id)} [data-nurly-results]`) || this.closest('.nurly-cgrid')?.querySelector('[data-nurly-results]');
    results?.classList.add('is-loading');
    try {
      const res = await fetch(url + sep + 'section_id=' + encodeURIComponent(id), { signal: this._ctrl.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[data-nurly-results]');
      if (fresh && results) results.replaceWith(fresh);
      const pill = fresh?.querySelector('template[data-nurly-pill]');
      const target = this.querySelector('[data-nurly-pill-target]');
      if (pill && target) target.textContent = pill.content.textContent.trim();
      // Disponibilité des options (compteurs à 0 → désactivées)
      doc.querySelectorAll('[data-nurly-facets-form] input[type="checkbox"]').forEach((n) => {
        const cur = [...this.form.querySelectorAll('input[type="checkbox"]')].find((i) => i.name === n.name && i.value === n.value);
        if (cur && !cur.checked) cur.disabled = n.disabled;
      });
      const clear = doc.querySelector('.nurly-drawer__btn--secondary');
      if (clear) this.querySelector('.nurly-drawer__btn--secondary')?.setAttribute('href', clear.getAttribute('href'));
      history.replaceState(history.state, '', url);
    } catch (err) {
      if (err.name !== 'AbortError') window.location.assign(url);
    } finally {
      document.querySelector('[data-nurly-results].is-loading')?.classList.remove('is-loading');
    }
  }
  open() {
    this.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
    document.documentElement.classList.add('nurly-lock');
    document.addEventListener('keydown', this.onKey);
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    setTimeout(() => this.querySelector('.nurly-drawer__close')?.focus(), 50);
  }
  close() {
    this.classList.remove('is-open');
    document.documentElement.classList.remove('nurly-lock');
    document.removeEventListener('keydown', this.onKey);
    const openers = document.querySelectorAll(`[aria-controls="${this.id}"]`);
    openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 300);
    openers[0]?.focus();
  }
}
if (!customElements.get('nurly-facets-drawer')) customElements.define('nurly-facets-drawer', NurlyFacetsDrawer);

// Délégation : les résultats sont remplacés après chaque filtre → écouteurs sur le document
if (!window.__nurlyCollectionBound) {
  window.__nurlyCollectionBound = true;
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-nurly-drawer-open]');
    if (!opener) return;
    document.getElementById(opener.getAttribute('aria-controls'))?.open();
  });
  document.addEventListener('change', (e) => {
    const select = e.target.closest('select[data-nurly-sort]');
    if (!select) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });
}
